import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import '../styles/pages/_modules.scss';
import '../styles/pages/_emotio-psycho-rational-resources.scss';
import { Brain, Heart, Lightbulb, Target, Smile, CheckCircle } from 'lucide-react';

// Import image using require to handle spaces in filename
const emotioWellnessImage = require('../assets/Emotio.png');

interface ModuleCard {
  id: string;
  title: string;
  category: 'emotioPsychoRationalM1' | 'emotioPsychoRationalM2' | 'emotioPsychoRationalM3' | 'emotioPsychoRationalM4' | 'emotioPsychoRationalM5';
  image: string;
  icon: React.ReactNode;
}

interface ModuleProgress {
  moduleId: string;
  currentStep: number;
  completed: boolean;
}

const EmotioPsychoRationalResourcesPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [progress, setProgress] = useState<ModuleProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user || !token) return;

      try {
        // Get progress for all modules
        const moduleIds = [
          'emotioPsychoRationalM1',
          'emotioPsychoRationalM2',
          'emotioPsychoRationalM3',
          'emotioPsychoRationalM4',
          'emotioPsychoRationalM5'
        ];
        const responses = await Promise.all(
          moduleIds.map(moduleId =>
            fetch(`${API_CONFIG.ENDPOINTS.MODULE_PROGRESS}/${moduleId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            })
          )
        );

        const results = await Promise.all(
          responses.map(async (response, index) => {
            if (response.ok) {
              const data = await response.json();
              return {
                moduleId: moduleIds[index],
                ...data.data
              };
            }
            return {
              moduleId: moduleIds[index],
              currentStep: 0,
              completed: false
            };
          })
        );

        setProgress(results);
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    fetchProgress();
  }, [user, token]);

  const handleModuleClick = (moduleId: string) => {
    switch (moduleId) {
      case 'm1':
        navigate('/panel/learn/emotio-psycho-rational-module-1');
        break;
      case 'm2':
        navigate('/panel/learn/emotio-psycho-rational-module-2');
        break;
      case 'm3':
        navigate('/panel/learn/emotio-psycho-rational-module-3');
        break;
      case 'm4':
        navigate('/panel/learn/emotio-psycho-rational-module-4');
        break;
      case 'm5':
        navigate('/panel/learn/emotio-psycho-rational-module-5');
        break;
      default:
        console.log(`Module ${moduleId} navigation not yet implemented`);
    }
  };

  const modules: ModuleCard[] = [
    {
      id: 'm1',
      title: 'Understanding the Emotio-Psycho-Rational Self',
      category: 'emotioPsychoRationalM1',
      image: emotioWellnessImage.default || emotioWellnessImage,
      icon: <Brain size={24} />,
    },
    {
      id: 'm2',
      title: 'Emotional Awareness and Regulation',
      category: 'emotioPsychoRationalM2',
      image: emotioWellnessImage.default || emotioWellnessImage,
      icon: <Heart size={24} />,
    },
    {
      id: 'm3',
      title: 'Managing Thoughts and Mental Patterns',
      category: 'emotioPsychoRationalM3',
      image: emotioWellnessImage.default || emotioWellnessImage,
      icon: <Lightbulb size={24} />,
    },
    {
      id: 'm4',
      title: 'Building Rational Strength and Resilience',
      category: 'emotioPsychoRationalM4',
      image: emotioWellnessImage.default || emotioWellnessImage,
      icon: <Target size={24} />,
    },
    {
      id: 'm5',
      title: 'Purpose, Satisfaction, and Meaning',
      category: 'emotioPsychoRationalM5',
      image: emotioWellnessImage.default || emotioWellnessImage,
      icon: <Smile size={24} />,
    }
  ];

  if (isLoading) {
    return (
      <div className={`resources-page ${isDarkMode ? 'dark' : ''}`} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className={`resources-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="module-resources_container">
        {/* Header Section */}
        <div className="module-resources_header">
          <div className="module-resources_header-content">
            <div className="module-resources_header-text">
              <h2>Emotio-Psycho-Rational Resources</h2>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="module-resources_modules">
          <div className="module-resources_modules-grid">
            {modules.length > 0 ? 
              modules.map((module: ModuleCard) => {
                const moduleProgress = progress.find(p => p.moduleId === module.category);
                const isCompleted = moduleProgress?.completed || false;
                return (
                  <div 
                    key={module.id} 
                    className={`module-resources_module-card ${isCompleted ? 'completed' : ''}`}
                    onClick={() => handleModuleClick(module.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleModuleClick(module.id);
                      }
                    }}
                  >
                    <div className="module-resources_module-image">
                      <img src={module.image} alt={module.title} />
                      {isCompleted && (
                        <div className="completion-overlay">
                          <CheckCircle 
                            className="check-icon"
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                          />
                        </div>
                      )}
                    </div>
                    <div className="module-resources_module-content">
                      <h3 className="module-resources_module-title">{module.title}</h3>
                    </div>
                  </div>
                );
              })
              : <p>No modules found.</p>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalResourcesPage;


